import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useProjectStore } from '../store/useProjectStore';
import { isValidUuid } from '../utils/uuid';
import { Project } from '../types';

const LS_PROJECTS_KEY = 'lean_workflow_projects';
const LS_PROJECTS_ORG_KEY = 'lean_workflow_projects_org';
const LS_MAP_SNAPSHOT_KEY = 'wf_active_map_session_snapshot';

export type WorkspaceProjectScope = 'project' | 'all';

const scopeStorageKey = (orgId: string) => `wf_workspace_scope_${orgId}`;
const dismissedStorageKey = (orgId: string) => `wf_workspace_dismissed_${orgId}`;

function readPersistedScope(orgId: string): WorkspaceProjectScope {
  try {
    return localStorage.getItem(scopeStorageKey(orgId)) === 'all' ? 'all' : 'project';
  } catch {
    return 'project';
  }
}

function readPersistedDismissed(orgId: string): string | null {
  try {
    const value = localStorage.getItem(dismissedStorageKey(orgId));
    return value && isValidUuid(value) ? value : null;
  } catch {
    return null;
  }
}

function persistWorkspaceScope(orgId: string, scope: WorkspaceProjectScope, dismissedProjectId: string | null) {
  try {
    localStorage.setItem(scopeStorageKey(orgId), scope);
    if (dismissedProjectId) {
      localStorage.setItem(dismissedStorageKey(orgId), dismissedProjectId);
    } else {
      localStorage.removeItem(dismissedStorageKey(orgId));
    }
  } catch {
    /* noop */
  }
}

function clearLocalProjectCache() {
  try {
    localStorage.removeItem(LS_PROJECTS_KEY);
    localStorage.removeItem('wf_current_project_id');
    localStorage.removeItem('wf_current_map_type');
    localStorage.removeItem(LS_MAP_SNAPSHOT_KEY);
  } catch {
    /* noop */
  }
}

function purgeLegacyMapSnapshot() {
  try {
    const raw = localStorage.getItem(LS_MAP_SNAPSHOT_KEY);
    if (!raw) return;
    const snapshot = JSON.parse(raw) as { projectId?: string | null };
    if (snapshot.projectId && !isValidUuid(snapshot.projectId)) {
      localStorage.removeItem(LS_MAP_SNAPSHOT_KEY);
    }
  } catch {
    localStorage.removeItem(LS_MAP_SNAPSHOT_KEY);
  }
}

interface ProjectContextType {
  projects: Project[];
  selectedProject: Project | null;
  setSelectedProject: (project: Project | null) => void;
  setProjects: (projects: Project[]) => void;
  updateProject: (id: string, updates: Partial<Project>) => void;
  isCreateActionModalOpen: boolean;
  setCreateActionModalOpen: (isOpen: boolean) => void;
  /** Escopo global: filtrar módulos pelo projeto ativo do mapa */
  workspaceScope: WorkspaceProjectScope;
  workspaceProjectId: string | null;
  workspaceProjectName: string | null;
  /** ID efetivo para queries de listagem — null quando usuário clicou em "Ver todos" */
  filterProjectId: string | null;
  filterProjectName: string | null;
  isProjectScopeActive: boolean;
  focusWorkspaceProject: (projectId: string, projectName?: string | null) => void;
  dismissProjectScope: () => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { workspace } = useWorkspace();
  const orgId = workspace?.organizationId;
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreateActionModalOpen, setCreateActionModalOpen] = useState(false);

  const [workspaceScope, setWorkspaceScope] = useState<WorkspaceProjectScope>('project');
  const [dismissedProjectId, setDismissedProjectId] = useState<string | null>(null);
  const [workspaceProjectId, setWorkspaceProjectId] = useState<string | null>(null);
  const [workspaceProjectName, setWorkspaceProjectName] = useState<string | null>(null);

  const workspaceRef = useRef({ scope: 'project' as WorkspaceProjectScope, dismissed: null as string | null });

  const focusWorkspaceProject = useCallback((projectId: string, projectName?: string | null) => {
    if (!orgId || !isValidUuid(projectId)) return;
    setWorkspaceProjectId(projectId);
    if (projectName !== undefined) setWorkspaceProjectName(projectName ?? null);
    setWorkspaceScope('project');
    setDismissedProjectId(null);
    workspaceRef.current = { scope: 'project', dismissed: null };
    persistWorkspaceScope(orgId, 'project', null);
  }, [orgId]);

  const dismissProjectScope = useCallback(() => {
    if (!orgId) return;
    const dismissed = workspaceProjectId;
    setWorkspaceScope('all');
    setDismissedProjectId(dismissed);
    workspaceRef.current = { scope: 'all', dismissed };
    persistWorkspaceScope(orgId, 'all', dismissed);
  }, [orgId, workspaceProjectId]);

  const filterProjectId = workspaceScope === 'project' ? workspaceProjectId : null;
  const filterProjectName = workspaceScope === 'project' ? workspaceProjectName : null;
  const isProjectScopeActive = workspaceScope === 'project' && !!workspaceProjectId;

  // Canvas local é por organização — cache antigo (sem tag de org) não deve sobrescrever o Supabase
  useEffect(() => {
    if (!orgId || !isValidUuid(orgId)) return;

    purgeLegacyMapSnapshot();
    const staleProjectId = localStorage.getItem('wf_current_project_id');
    if (staleProjectId && !isValidUuid(staleProjectId)) {
      localStorage.removeItem('wf_current_project_id');
    }

    const cachedOrg = localStorage.getItem(LS_PROJECTS_ORG_KEY);
    const saved = localStorage.getItem(LS_PROJECTS_KEY);
    const staleCache = (cachedOrg && cachedOrg !== orgId) || (!cachedOrg && !!saved);

    if (staleCache) {
      clearLocalProjectCache();
      setProjects([]);
      setSelectedProject(null);
      setWorkspaceProjectId(null);
      setWorkspaceProjectName(null);
      setWorkspaceScope('project');
      setDismissedProjectId(null);
      workspaceRef.current = { scope: 'project', dismissed: null };
      localStorage.setItem(LS_PROJECTS_ORG_KEY, orgId);
      return;
    }

    localStorage.setItem(LS_PROJECTS_ORG_KEY, orgId);

    const persistedScope = readPersistedScope(orgId);
    const persistedDismissed = readPersistedDismissed(orgId);
    setWorkspaceScope(persistedScope);
    setDismissedProjectId(persistedDismissed);
    workspaceRef.current = { scope: persistedScope, dismissed: persistedDismissed };

    const mapProjectId = localStorage.getItem('wf_current_project_id');
    if (mapProjectId && isValidUuid(mapProjectId)) {
      setWorkspaceProjectId(mapProjectId);
      const displayName = useProjectStore.getState().currentMapDisplayName;
      if (displayName) setWorkspaceProjectName(displayName);
    }

    if (!saved) return;

    try {
      const parsed = JSON.parse(saved) as Project[];
      const validOnly = parsed.filter((p) => isValidUuid(p.id));
      const migratedProjects = validOnly.map((p) => ({
        ...p,
        status: p.status || 'draft',
      }));
      setProjects(migratedProjects);
      localStorage.setItem(LS_PROJECTS_KEY, JSON.stringify(migratedProjects));
    } catch (e) {
      console.error('Erro ao carregar projetos locais', e);
      clearLocalProjectCache();
    }
  }, [orgId]);

  // Sincroniza projeto aberto no mapa → contexto de workspace (filtro cross-módulo)
  useEffect(() => {
    if (!orgId) return;

    const syncFromStore = (id: string | null, name: string | null) => {
      if (!id || !isValidUuid(id)) return;
      setWorkspaceProjectId(id);
      if (name) setWorkspaceProjectName(name);

      const { dismissed, scope } = workspaceRef.current;
      if (id !== dismissed) {
        setWorkspaceScope('project');
        setDismissedProjectId(null);
        workspaceRef.current = { scope: 'project', dismissed: null };
        persistWorkspaceScope(orgId, 'project', null);
      } else if (scope === 'all') {
        setWorkspaceScope('all');
      }
    };

    const initial = useProjectStore.getState();
    syncFromStore(initial.currentProjectId, initial.currentMapDisplayName);

    return useProjectStore.subscribe((state, prevState) => {
      if (state.currentProjectId === prevState.currentProjectId
        && state.currentMapDisplayName === prevState.currentMapDisplayName) {
        return;
      }
      syncFromStore(state.currentProjectId, state.currentMapDisplayName);
    });
  }, [orgId]);

  const updateProject = (id: string, updates: Partial<Project>) => {
    setProjects(prev => {
      const newList = prev.map(p => p.id === id ? { ...p, ...updates } : p);
      localStorage.setItem(LS_PROJECTS_KEY, JSON.stringify(newList));
      if (orgId) localStorage.setItem(LS_PROJECTS_ORG_KEY, orgId);
      return newList;
    });

    if (selectedProject?.id === id) {
      setSelectedProject(prev => prev ? { ...prev, ...updates } : null);
    }
    if (workspaceProjectId === id && updates.nome_projeto) {
      setWorkspaceProjectName(updates.nome_projeto);
    }
  };

  const value = useMemo<ProjectContextType>(() => ({
    projects,
    selectedProject,
    setSelectedProject,
    setProjects,
    updateProject,
    isCreateActionModalOpen,
    setCreateActionModalOpen,
    workspaceScope,
    workspaceProjectId,
    workspaceProjectName,
    filterProjectId,
    filterProjectName,
    isProjectScopeActive,
    focusWorkspaceProject,
    dismissProjectScope,
  }), [
    projects,
    selectedProject,
    isCreateActionModalOpen,
    workspaceScope,
    workspaceProjectId,
    workspaceProjectName,
    filterProjectId,
    filterProjectName,
    isProjectScopeActive,
    focusWorkspaceProject,
    dismissProjectScope,
  ]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjectContext deve ser usado dentro de um ProjectProvider');
  }
  return context;
};

/** Atalho para filtro de projeto compartilhado entre módulos operacionais. */
export function useWorkspaceProjectFilter() {
  const {
    filterProjectId,
    filterProjectName,
    isProjectScopeActive,
    workspaceProjectId,
    workspaceProjectName,
    focusWorkspaceProject,
    dismissProjectScope,
  } = useProjectContext();

  return {
    filterProjectId,
    filterProjectName,
    isProjectScopeActive,
    workspaceProjectId,
    workspaceProjectName,
    focusWorkspaceProject,
    dismissProjectScope,
  };
};
